import {
    Dialog,
    DialogBackdrop,
    DialogPanel,
    TransitionChild,
} from "@headlessui/react";
import { ChevronLeftIcon as ChevronLeftMiniIcon, ChevronRightIcon as ChevronRightMiniIcon } from "@heroicons/react/16/solid";
import {
    AdjustmentsHorizontalIcon,
    ArrowPathIcon,
    ArrowPathRoundedSquareIcon,
    BackwardIcon,
    Bars3Icon,
    CheckCircleIcon,
    ChevronDoubleDownIcon,
    ChevronDoubleLeftIcon,
    ChevronDoubleRightIcon,
    ChevronDoubleUpIcon,
    ChevronLeftIcon,
    ChevronRightIcon,
    ClockIcon,
    CloudArrowUpIcon,
    FilmIcon,
    ForwardIcon,
    FunnelIcon,
    ListBulletIcon,
    PlayIcon,
    SignalIcon,
    VideoCameraIcon,
    XCircleIcon,
    XMarkIcon,
} from "@heroicons/react/24/outline";
import Hls from "hls.js";
import { sortBy } from "lodash";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useCookies } from "react-cookie";
import ReactPlayer from "react-player/lazy";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Tooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";
import { useLanguage } from "../../context/Language/LanguageContext";
import { getLatestStats, getSession, storeSession } from "../../context/VBLiveAPI/VBLiveAPIAction";
import { useAuthStatus } from "../hooks/useAuthStatus";
import { formatJumpHeightM, getLiveRefreshInterval, getRotationLabels } from "../utils/Constants";
import { calculateDVWStats, DVEventString, initWithDVWCompressedBuffer } from "../utils/DVWFile";
import { calculatePSVBStats, initWithPSVBCompressedBuffer, parseLatestPSVBStats } from "../utils/PSVBFile";
import { calculateSideoutStats } from "../utils/StatsItem";
import {
    getEventInfo,
    getEventStringColor,
    getTimingForEvent,
    makeFilename,
    saveToPC,
    toHlsStreamUrl
} from "../utils/Utils";
import { myzip } from "../utils/zip";
import EventsList from "./EventsList";
import Timing from "./Timing";
import VideoFilters from "./VideoFilters";

// Assumed 30fps for the transport toolbar's single-frame rewind/forward step.
const FRAME_SECONDS = 1 / 30;

// Once HLS playback falls this many seconds behind the live edge, surface a
// "Go Live" control (see the HLS effect + goLive()).
const kLiveDriftThreshold = 20;
// hls.js's default initial distance behind the live edge (matches
// liveSyncDuration below) — the assumed baseline before any manual seek.
const kDefaultLiveDrift = 5;
// How many seconds of UNINTENDED extra drift (beyond whatever offset from
// live the user last intentionally chose — see baselineDriftRef) are
// tolerated before we silently seek back to that intended offset. Network
// stalls/rebuffering are the usual cause; without this the gap only grows
// because liveMaxLatencyDuration is disabled below (see the HLS effect).
const kAutoCatchupExtra = 30;

function VideoAnalysis() {
  // function VideoAnalysis({ match, selectedGame }) {
  const location = useLocation();
  // `matches` must never be undefined: several hooks below (useMemo/
  // useCallback/useEffect) unconditionally read matches.length/matches[0]
  // on every render, regardless of the "no data" guard further down (hooks
  // can't be called conditionally).
  const { matches = [], team, selectedGame, sessionId, isLive: isLiveProp } = location.state ?? {};
  const navigate = useNavigate();
  const { t } = useLanguage();
  const playerRef = useRef();
  const hlsVideoRef = useRef(null); // raw <video> element used by the HLS player
  const hlsInstanceRef = useRef(null); // direct hls.js instance for seek control
  // Seconds behind the live edge the user last intentionally chose (updated on
  // every manual seek — see seekToAndPlay/hlsReady/goLive). The drift watchdog
  // in the HLS effect only auto-corrects drift that grows beyond this baseline.
  const baselineDriftRef = useRef(kDefaultLiveDrift);
  const dvRef = useRef();
  const radarRef = useRef();
  const { currentUser } = useAuthStatus();
  const [showRadarFile, setShowRadarFile] = useState(false);
  const [radarFileName, setRadarFileName] = useState(null);
  const [cookies, setCookie] = useCookies(["videofile"]);
  const [videoInfo, setVideoInfo] = useState(null);
  const [videoOnlineUrl, setVideoOnlineUrl] = useState(null);
  const [videoFilePath, setVideoFilePath] = useState(null);
  const [videoFileName, setVideoFileName] = useState(null);
  const [videoFileObject, setVideoFileObject] = useState(null);
  const [selectedSet, setSelectedSet] = useState(1);
  const [selectedMatchGuid, setSelectedMatchGuid] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [currentMatch, setCurrentMatch] = useState(null);
  const [startVideoTimeSeconds, setStartVideoTimeSeconds] = useState(null);
  const [videoOffset, setVideoOffset] = useState(0);
  const [currentVideoTime, setCurrentVideoTime] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isCollapseAll, setIsCollapseAll] = useState(true);
  const [, forceUpdate] = useState(0);
  const [allFilters, setAllFilters] = useState(null);
  const [teamAPlayers, setTeamAPlayers] = useState(null);
  const [teamBPlayers, setTeamBPlayers] = useState(null);
  const [selectedTeamAPlayers, setSelectedTeamAPlayers] = useState(null);
  const [selectedTeamBPlayers, setSelectedTeamBPlayers] = useState(null);
  const [attackCombos, setAttackCombos] = useState(null);
  const [setterCalls, setSetterCalls] = useState(null);
  const [selectedAttackCombos, setSelectedAttackCombos] = useState(null);
  const [selectedSetterCalls, setSelectedSetterCalls] = useState(null);
  const [games, setGames] = useState(null);
  const [rotations, setRotations] = useState(null);
  const [selectedGames, setSelectedGames] = useState(null);
  const [selectedRotations, setSelectedRotations] = useState(null);
  const [leadTimes, setLeadTimes] = useState([0, 0, 3, 3, 3, 3]);
  const [durations, setDurations] = useState([8, 8, 8, 8, 8, 8]);
  const [filteredEvents, setFilteredEvents] = useState(null);
  const [playlistEvents, setPlaylistEvents] = useState([]);
  const menuRef = useRef();
  const [menuOpen, setMenuOpen] = useState(false);
  const wref = useRef(null);
  const [contentsHeight, setContentsHeight] = useState(0);
  const [isYoutube, setIsYoutube] = useState(false);
  // Tracks the player's REAL play/pause state (incl. pauses made via the
  // native controls) so ReactPlayer's `playing` prop — and therefore the
  // underlying <video>'s `autoplay` attribute — doesn't silently resume a
  // paused video after a seek forces a rebuffer (seen with some CDN-hosted
  // mp4s that don't serve byte-range requests as cleanly as YouTube).
  const [isPlaying, setIsPlaying] = useState(true);
  const [videoWidth, setVideoWidth] = useState(0);
  const [windowHeight, setWindowHeight] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [showFiltersModal, setShowFiltersModal] = useState(false);
  const [showTimingModal, setShowTimingModal] = useState(false);
  // Event currently shown in the jump-info overlay as the video plays back
  // (distinct from `selectedEvent`, which is whatever the user last clicked
  // in the events list) -- see the playback-progress effect below.
  const [jumpOverlayEvent, setJumpOverlayEvent] = useState(null);
  const jumpOverlayTimerRef = useRef(null);
  const lastVideoProgressRef = useRef(null);

  // True once HLS playback has fallen more than kLiveDriftThreshold seconds
  // behind the live edge (see the HLS effect below) — surfaces a "Go Live"
  // control so the user can resync in one click.
  const [isBehindLive, setIsBehindLive] = useState(false);

  // Live-session auto-refresh state
  const [isLive, setIsLive] = useState(!!isLiveProp);
  const [liveAutoRefresh, setLiveAutoRefresh] = useState(true);
  const [liveCounter, setLiveCounter] = useState(0);
  const liveRefreshInterval = getLiveRefreshInterval();
  const liveCountRef = useRef(0);
  // Separate state copy of matches so EventsList re-renders when data changes
  const [currentMatches, setCurrentMatches] = useState(matches);
  // Tracks event IDs already in the list so refresh only appends genuinely new events
  const knownEventIdsRef = useRef(null);

  // Returns the current HLS live edge in seconds, or null if it can't be
  // determined yet (used by the drift watchdog, seekToAndPlay, hlsReady, goLive).
  const getHlsLiveEdge = () => {
    const vid = hlsVideoRef.current;
    const hls = hlsInstanceRef.current;
    if (!vid) return null;
    if (hls && typeof hls.liveSyncPosition === "number") return hls.liveSyncPosition;
    if (vid.seekable && vid.seekable.length > 0) return vid.seekable.end(vid.seekable.length - 1);
    return null;
  };

  // Direct hls.js lifecycle: create/destroy the instance whenever the HLS source
  // changes.  Owning the instance lets us call stopLoad()+startLoad(pos) on every
  // seek so hls.js fetches from the requested position instead of the live edge.
  useEffect(() => {
    if (!videoFilePath?.endsWith(".m3u8")) {
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy();
        hlsInstanceRef.current = null;
      }
      return;
    }
    const vid = hlsVideoRef.current;
    if (!vid) return;

    if (hlsInstanceRef.current) {
      hlsInstanceRef.current.destroy();
      hlsInstanceRef.current = null;
    }
    baselineDriftRef.current = kDefaultLiveDrift;

    if (!Hls.isSupported()) {
      // Safari: native HLS support — just set src directly.
      vid.src = videoFilePath;
    } else {
      const hls = new Hls({
        backBufferLength: 7200,
        // Use a very large latency window so hls.js never auto-jumps to the
        // live edge after a historical seek.  Must satisfy the hls.js invariant:
        // liveMaxLatencyDuration > liveSyncDuration.
        liveSyncDuration: 5,
        liveMaxLatencyDuration: 86400,
        // Never send credentials: the MediaMTX HLS relay needs no auth and serves
        // its session via a `?session=` query param.  A credentialed cross-origin
        // request forces MediaMTX down its `SameSite=None; Secure` cookie path,
        // which cannot round-trip over plain HTTP on the LAN → 401 Unauthorized.
        xhrSetup: (xhr) => {
          xhr.withCredentials = false;
        },
      });
      hls.loadSource(videoFilePath);
      hls.attachMedia(vid);
      hlsInstanceRef.current = hls;
    }

    // liveMaxLatencyDuration is deliberately huge above so hls.js never auto-
    // snaps back to the live edge (that would fight `seekToAndPlay` whenever
    // the user reviews a historical event).  The tradeoff: if playback ever
    // stalls/rebuffers while genuinely watching live, nothing corrects the
    // drift and it silently grows without bound (symptom: broadcast is N
    // minutes in, the player shows N-minus-several and the gap keeps
    // widening).  We can't tell "user is reviewing an old event on purpose"
    // from "user fell behind live by accident", so instead of snapping to the
    // live edge we track `baselineDriftRef` — the offset from live the user
    // last intentionally chose (updated by seekToAndPlay/hlsReady/goLive) —
    // and only correct drift that grows *beyond* that intended offset by more
    // than kAutoCatchupExtra, which can only be unintended stall time.
    const driftCheck = setInterval(() => {
      const v = hlsVideoRef.current;
      if (!v || v.paused) return;
      const edge = getHlsLiveEdge();
      if (edge == null) return;
      const drift = edge - v.currentTime;
      const extra = drift - baselineDriftRef.current;
      if (extra > kAutoCatchupExtra) {
        const target = Math.max(0, edge - baselineDriftRef.current);
        v.currentTime = target;
        v.play().catch(() => {});
        setIsBehindLive(false);
      } else {
        setIsBehindLive(drift > kLiveDriftThreshold);
      }
    }, 5000);

    return () => {
      clearInterval(driftCheck);
      if (hlsInstanceRef.current) {
        hlsInstanceRef.current.destroy();
        hlsInstanceRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [videoFilePath]);

  const eventTypes = [
    { value: 0, label: t("All Types") },
    { value: 1, label: t("Serve") },
    { value: 2, label: t("Serve-Receive") },
    { value: 3, label: t("Set") },
    { value: 20, label: t("Setter's Call") },
    { value: 4, label: t("Spike") },
    { value: 5, label: t("Block") },
    { value: 6, label: t("Defence") },
  ];
  const [selectedEventTypes, setSelectedEventTypes] = useState([eventTypes[0]]);
  const eventResults = [
    { value: 0, label: t("All Results") },
    { value: 1, label: "=" },
    { value: 2, label: "/" },
    { value: 3, label: "-" },
    { value: 4, label: "!" },
    { value: 5, label: "+" },
    { value: 6, label: "#" },
  ];
  const [selectedEventResults, setSelectedEventResults] = useState([
    eventResults[0],
  ]);

  const attackTypes = [
    { value: 0, label: t("All Types") },
    { value: 1, label: t("Hard") },
    { value: 2, label: t("Soft") },
    { value: 3, label: t("Tip") },
    { value: 4, label: t("Not Specified") },
  ];
  const [selectedAttackTypes, setSelectedAttackTypes] = useState([
    attackTypes[0],
  ]);

  const hitTypes = [
    { value: 0, label: t("All Types") },
    { value: 1, label: t("H - High") },
    { value: 2, label: t("M- Medium") },
    { value: 3, label: t("Q - Quick") },
    { value: 4, label: t("T - Tense") },
    { value: 5, label: t("U - Super") },
    { value: 6, label: t("F - Fast") },
    { value: 7, label: t("O - Other") },
  ];
  const [selectedHitTypes, setSelectedHitTypes] = useState([hitTypes[0]]);

  const blockTypes = [
    { value: 6, label: t("All Types") },
    { value: 0, label: t("B0 - No Block") },
    { value: 1, label: t("B1 - 1 Block") },
    { value: 2, label: t("B2 - 2 Blocks") },
    { value: 3, label: t("B3 - 3 Blocks") },
    { value: 4, label: t("BS - Seams") },
    { value: 5, label: t("Not Specified") },
  ];
  const [selectedBlockTypes, setSelectedBlockTypes] = useState([blockTypes[0]]);

  const powerPlayOptions = [
    { value: 0, label: t("All Events") },
    { value: 1, label: t("Power Play Only") },
    { value: 2, label: t("No Power Play") },
  ];
  const [selectedPowerPlay, setSelectedPowerPlay] = useState([powerPlayOptions[0]]);

  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);

  // the required distance between touchStart and touchEnd to be detected as a swipe
  const minSwipeDistance = 50;

  const onTouchStart = (e) => {
    setTouchEnd(null); // otherwise the swipe is fired even with usual touch events
    setTouchStart(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEnd(e.targetTouches[0].clientX);

  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    if (isLeftSwipe || isRightSwipe)
      console.log("swipe", isLeftSwipe ? "left" : "right");
    // add your conditional logic here
  };

  const onSaveToDatabase = async () => {
    const ret = await storeSession(matches[0], currentUser);
    if (ret === 0) {
      toast.error("Error uploading session");
    } else {
      toast.success("Session uploaded successfully");
    }
  };

  // function handleSelectEventTypes(data) {
  //   if (data.length === 0) {
  //     setSelectedEventTypes(data);
  //     return;
  //   }
  //   if (data[0].value === 0 && data.length > 1) {
  //     var ddd = [];
  //     for (var nd = 1; nd < data.length; nd++) {
  //       ddd.push(data[nd]);
  //     }
  //     setSelectedEventTypes(ddd);
  //     return;
  //   } else if (data[data.length - 1].value === 0 && data.length > 1) {
  //     setSelectedEventTypes([data[data.length - 1]]);
  //     return;
  //   }
  //   setSelectedEventTypes(data);
  // }

  // function handleSelectGames(data) {
  //   if (data.length === 0) {
  //     setSelectedGames(data);
  //     return;
  //   }
  //   if (data[0].value === 0 && data.length > 1) {
  //     var ddd = [];
  //     for (var nd = 1; nd < data.length; nd++) {
  //       ddd.push(data[nd]);
  //     }
  //     setSelectedGames(ddd);
  //     return;
  //   } else if (data[data.length - 1].value === 0 && data.length > 1) {
  //     setSelectedGames([data[data.length - 1]]);
  //     return;
  //   }
  //   setSelectedGames(data);
  // }

  // function handleSelectRotations(data) {
  //   if (data.length === 0) {
  //     setSelectedRotations(data);
  //     return;
  //   }
  //   if (data[0].value === 0 && data.length > 1) {
  //     var ddd = [];
  //     for (var nd = 1; nd < data.length; nd++) {
  //       ddd.push(data[nd]);
  //     }
  //     setSelectedRotations(ddd);
  //     return;
  //   } else if (data[data.length - 1].value === 0 && data.length > 1) {
  //     setSelectedRotations([data[data.length - 1]]);
  //     return;
  //   }
  //   setSelectedRotations(data);
  // }

  // function handleSelectEventResults(data) {
  //   if (data.length === 0) {
  //     setSelectedEventResults(data);
  //     return;
  //   }
  //   if (data[0].value === 0 && data.length > 1) {
  //     var ddd = [];
  //     for (var nd = 1; nd < data.length; nd++) {
  //       ddd.push(data[nd]);
  //     }
  //     setSelectedEventResults(ddd);
  //     return;
  //   } else if (data[data.length - 1].value === 0 && data.length > 1) {
  //     setSelectedEventResults([data[data.length - 1]]);
  //     return;
  //   }
  //   setSelectedEventResults(data);
  // }

  // function handleSelectAttackTypes(data) {
  //   if (data.length === 0) {
  //     setSelectedAttackTypes(data);
  //     return;
  //   }
  //   if (data[0].value === 0 && data.length > 1) {
  //     var ddd = [];
  //     for (var nd = 1; nd < data.length; nd++) {
  //       ddd.push(data[nd]);
  //     }
  //     setSelectedAttackTypes(ddd);
  //     return;
  //   } else if (data[data.length - 1].value === 0 && data.length > 1) {
  //     setSelectedAttackTypes([data[data.length - 1]]);
  //     return;
  //   }
  //   setSelectedAttackTypes(data);
  // }

  // function handleSelectHitTypes(data) {
  //   if (data.length === 0) {
  //     setSelectedHitTypes(data);
  //     return;
  //   }
  //   if (data[0].value === 0 && data.length > 1) {
  //     var ddd = [];
  //     for (var nd = 1; nd < data.length; nd++) {
  //       ddd.push(data[nd]);
  //     }
  //     setSelectedHitTypes(ddd);
  //     return;
  //   } else if (data[data.length - 1].value === 0 && data.length > 1) {
  //     setSelectedHitTypes([data[data.length - 1]]);
  //     return;
  //   }
  //   setSelectedHitTypes(data);
  // }

  // function handleSelectBlockTypes(data) {
  //   if (data.length === 0) {
  //     setSelectedBlockTypes(data);
  //     return;
  //   }
  //   if (data[0].value === 6 && data.length > 1) {
  //     var ddd = [];
  //     for (var nd = 1; nd < data.length; nd++) {
  //       ddd.push(data[nd]);
  //     }
  //     setSelectedBlockTypes(ddd);
  //     return;
  //   } else if (data[data.length - 1].value === 6 && data.length > 1) {
  //     setSelectedBlockTypes([data[data.length - 1]]);
  //     return;
  //   }
  //   setSelectedBlockTypes(data);
  // }

  // function handleSelectAttackCombos(data) {
  //   if (data.length === 0) {
  //     setSelectedAttackCombos(data);
  //     return;
  //   }
  //   if (data[0].value === 0 && data.length > 1) {
  //     var ddd = [];
  //     for (var nd = 1; nd < data.length; nd++) {
  //       ddd.push(data[nd]);
  //     }
  //     setSelectedAttackCombos(ddd);
  //     return;
  //   } else if (data[data.length - 1].value === 0 && data.length > 1) {
  //     setSelectedAttackCombos([data[data.length - 1]]);
  //     return;
  //   }
  //   setSelectedAttackCombos(data);
  // }

  // function handleSelectSetterCalls(data) {
  //   if (data.length === 0) {
  //     setSelectedSetterCalls(data);
  //     return;
  //   }
  //   if (data[0].value === 0 && data.length > 1) {
  //     var ddd = [];
  //     for (var nd = 1; nd < data.length; nd++) {
  //       ddd.push(data[nd]);
  //     }
  //     setSelectedSetterCalls(ddd);
  //     return;
  //   } else if (data[data.length - 1].value === 0 && data.length > 1) {
  //     setSelectedSetterCalls([data[data.length - 1]]);
  //     return;
  //   }
  //   setSelectedSetterCalls(data);
  // }

  // function handleSelectTeamAPlayers(data) {
  //   if (data.length === 0) {
  //     setSelectedTeamAPlayers(data);
  //     return;
  //   }
  //   if (data[0].value === 0 && data.length > 1) {
  //     var ddd = [];
  //     for (var nd = 1; nd < data.length; nd++) {
  //       ddd.push(data[nd]);
  //     }
  //     setSelectedTeamAPlayers(ddd);
  //     return;
  //   } else if (data[data.length - 1].value === 0 && data.length > 1) {
  //     setSelectedTeamAPlayers([data[data.length - 1]]);
  //     return;
  //   }
  //   setSelectedTeamAPlayers(data);
  // }

  // function handleSelectTeamBPlayers(data) {
  //   if (data.length === 0) {
  //     setSelectedTeamBPlayers(data);
  //     return;
  //   }
  //   if (data[0].value === 0 && data.length > 1) {
  //     var ddd = [];
  //     for (var nd = 1; nd < data.length; nd++) {
  //       ddd.push(data[nd]);
  //     }
  //     setSelectedTeamBPlayers(ddd);
  //     return;
  //   } else if (data[data.length - 1].value === 0 && data.length > 1) {
  //     setSelectedTeamBPlayers([data[data.length - 1]]);
  //     return;
  //   }
  //   setSelectedTeamBPlayers(data);
  // }

  const onFilters = (fes) => {
    setSelectedTeamAPlayers(fes ? fes.teamAPlayers : []);
    setSelectedTeamBPlayers(fes ? fes.teamBPlayers : []);
    setSelectedGames(fes ? fes.games : []);
    setSelectedRotations(fes ? fes.rotations : []);
    setSelectedEventTypes(fes ? fes.eventTypes : []);
    setSelectedEventResults(fes ? fes.eventResults : []);
    setSelectedAttackTypes(fes ? fes.attackTypes : []);
    setSelectedHitTypes(fes ? fes.hitTypes : []);
    setSelectedBlockTypes(fes ? fes.blockTypes : []);
    setSelectedAttackCombos(fes ? fes.attackCombos : []);
    setSelectedSetterCalls(fes ? fes.setterCalls : []);
    setSelectedPowerPlay(fes ? fes.powerPlay : []);
    setAllFilters(fes);
    setShowFiltersModal(false);
  };

  const onDoFilters = () => {
    setShowFiltersModal(false);
    setAllFilters({
      teamAPlayers: selectedTeamAPlayers,
      teamBPlayers: selectedTeamBPlayers,
      eventTypes: selectedEventTypes,
      eventResults: selectedEventResults,
      attackTypes: selectedAttackTypes,
      hitTypes: selectedHitTypes,
      blockTypes: selectedBlockTypes,
      attackCombos: selectedAttackCombos,
      setterCalls: selectedSetterCalls,
      games: selectedGames,
      rotations: selectedRotations,
      powerPlay: selectedPowerPlay,
    });
  };

  const teamPlayersList = (team) => {
    var pls = [{ value: 0, label: t("All Players") }];
    for (var np = 0; np < team.players.length; np++) {
      const pl = team.players[np];
      const plname =
        pl.shirtNumber + ". " + pl.FirstName + " " + pl.LastName.toUpperCase();
      pls.push({ value: np + 1, label: plname, guid: pl.Guid });
    }
    return pls;
  };

  // Seek to `seconds` and keep playing from that position.
  //
  // HLS (.m3u8) — set currentTime directly on the raw <video> element.
  //   We deliberately do NOT call hls.stopLoad()/startLoad() here: stopLoad()
  //   also halts the periodic manifest-refresh poll that advances the live DVR
  //   window, causing the duration to freeze.  Instead we rely on the
  //   liveMaxLatencyDuration:86400 config to prevent hls.js from snapping back
  //   to the live edge after the seek.
  //
  // YouTube — drive the internal YT player API directly.
  //
  // Everything else — set currentTime on the underlying <video> and call play()
  //   explicitly so the video doesn't freeze at the seeked frame.
  const seekToAndPlay = (seconds) => {
    if (seconds == null) return;
    const pos = Math.max(0, seconds);
    if (!isFinite(pos)) return; // NaN / Infinity would crash HTMLMediaElement.currentTime

    if (videoFilePath?.endsWith(".m3u8")) {
      const vid = hlsVideoRef.current;
      if (vid) {
        vid.currentTime = pos;
        const edge = getHlsLiveEdge();
        if (edge != null) baselineDriftRef.current = Math.max(0, edge - pos);
        vid.play().catch(() => {});
      }
      return;
    }

    if (isYoutube) {
      const yt = playerRef.current?.getInternalPlayer();
      if (yt?.seekTo) { yt.seekTo(pos, /* allowSeekAhead */ true); }
      return;
    }

    // Native HTML5 path: bypass ReactPlayer wrapper to avoid internal pause/resume lag.
    const vid = playerRef.current?.getInternalPlayer();
    if (vid && vid.currentTime !== undefined) {
      vid.currentTime = pos;
      vid.play().catch(() => {});
    } else {
      playerRef.current?.seekTo(pos, "seconds");
    }
  };

  // Same seek logic as seekToAndPlay but WITHOUT the forced play() calls --
  // used by the transport toolbar so a paused video stays paused after a
  // rewind/forward nudge instead of always resuming playback.
  const seekWithoutForcingPlay = (seconds) => {
    if (seconds == null) return;
    const pos = Math.max(0, seconds);
    if (!isFinite(pos)) return;

    if (videoFilePath?.endsWith(".m3u8")) {
      const vid = hlsVideoRef.current;
      if (vid) {
        vid.currentTime = pos;
        const edge = getHlsLiveEdge();
        if (edge != null) baselineDriftRef.current = Math.max(0, edge - pos);
      }
      return;
    }

    if (isYoutube) {
      // YouTube's own seekTo already preserves whatever play/pause state the
      // player was already in, so no forced play() is needed here either.
      const yt = playerRef.current?.getInternalPlayer();
      if (yt?.seekTo) { yt.seekTo(pos, /* allowSeekAhead */ true); }
      return;
    }

    const vid = playerRef.current?.getInternalPlayer();
    if (vid && vid.currentTime !== undefined) {
      vid.currentTime = pos;
    } else {
      playerRef.current?.seekTo(pos, "seconds");
    }
  };

  // Transport toolbar buttons (±60s/10s/1s/1 frame) drive seeks relative to
  // the last known videoProgress rather than the player's own live position.
  const seekRelative = (delta) => {
    const newTime = videoProgress + delta;
    setVideoProgress(newTime);
    seekWithoutForcingPlay(newTime);
  };

  const goToVideoPosition = (videoTime) => {
    seekToAndPlay(videoTime);
  };

  const playerReady = () => {
    if (!isReady) {
      setIsReady(true);
      // Only seek if there is an explicit pending position (multi-match video switch).
      // Do NOT call seekTo(null) — ReactPlayer converts null to NaN which sets
      // video.currentTime = NaN, making the video unplayable.
      if (currentVideoTime !== null) {
        playerRef.current.seekTo(currentVideoTime, "seconds");
        setCurrentVideoTime(null);
      }
    }
  };

  // Equivalent of playerReady for the ReactHlsPlayer <video> element.
  // Uses onCanPlay (not onLoadedMetadata) because live HLS streams have
  // duration=Infinity and some browsers never fire loadedmetadata for them.
  // Guards with vid.paused so double-firing doesn't interrupt a seek in progress.
  const hlsReady = () => {
    const vid = hlsVideoRef.current;
    if (!vid || !vid.paused) return; // already playing
    setIsReady(true);
    if (currentVideoTime !== null) {
      const pos = Math.max(0, currentVideoTime);
      vid.currentTime = pos;
      const edge = getHlsLiveEdge();
      if (edge != null) baselineDriftRef.current = Math.max(0, edge - pos);
      setCurrentVideoTime(null);
    }
    vid.play().catch(() => {});
  };

  // Jumps the HLS <video> to the live edge (see the drift-detection interval
  // in the HLS effect + the "Go Live" control that calls this).
  const goLive = () => {
    const vid = hlsVideoRef.current;
    if (!vid) return;
    const edge = getHlsLiveEdge();
    if (edge == null) return;
    vid.currentTime = edge;
    baselineDriftRef.current = kDefaultLiveDrift;
    setVideoProgress(edge);
    setIsBehindLive(false);
    vid.play().catch(() => {});
  };

  const closeEventsList = () => {
    document.getElementById("my-drawer-3").checked = false;
  };

  const showEventsList = () => {
    document.getElementById("my-drawer-3").checked = true;
  };

  const fileObjectToJsonString = (fileObject) => {
    fileObject.toJSON = function () {
      return {
        lastModified: fileObject.lastModified,
        lastModifiedDate: fileObject.lastModifiedDate,
        name: fileObject.name,
        size: fileObject.size,
        type: fileObject.type,
      };
    };
    return JSON.stringify(fileObject);
  };

  const JsonStringToFileObject = () => {
    var fileObject;
  };

  // Seconds-into-video for an event, independent of its leadTime (used for
  // seeking slightly early) -- the actual on-screen instant the event/jump
  // happened, for the playback-progress jump-overlay effect below.
  const videoPositionForEvent = (ev) => {
    if (ev.VideoPosition !== undefined && ev.VideoPosition !== 0) {
      return ev.VideoPosition;
    }
    if (
      startVideoTimeSeconds !== null &&
      startVideoTimeSeconds !== "-1" &&
      startVideoTimeSeconds !== -1 &&
      ev.TimeStamp
    ) {
      const secondsSinceEpoch = Math.round(ev.TimeStamp.getTime() / 1000);
      const offset = videoOffset !== null && videoOffset >= 0 ? videoOffset : 0;
      return secondsSinceEpoch - startVideoTimeSeconds + offset;
    }
    return null;
  };

  // Shows the jump-info overlay for `ev` and (re)starts its 3-second
  // auto-hide timer -- a newly-shown jump cancels any pending hide from a
  // previous one, so info only disappears early when ANOTHER jump replaces
  // it, matching "leave on screen for 3 seconds or until the next jump".
  const showJumpOverlay = (ev) => {
    setJumpOverlayEvent(ev);
    if (jumpOverlayTimerRef.current) clearTimeout(jumpOverlayTimerRef.current);
    jumpOverlayTimerRef.current = setTimeout(() => {
      setJumpOverlayEvent(null);
    }, 3000);
  };

  useEffect(() => {
    return () => {
      if (jumpOverlayTimerRef.current) clearTimeout(jumpOverlayTimerRef.current);
    };
  }, []);

  // All of this match's events carrying synced Catapult jump data (see
  // CatapultReport/enrichMatchWithCatapultData), with their video position
  // pre-resolved -- single-match only, same scope the Catapult tab itself is
  // restricted to.
  const jumpEvents = useMemo(() => {
    if (matches.length !== 1 || !matches[0]?.events) return [];
    return matches[0].events
      .filter((ev) => ev.imaHeightM != null)
      .map((ev) => ({ ev, pos: videoPositionForEvent(ev) }))
      .filter((x) => x.pos != null)
      .sort((a, b) => a.pos - b.pos);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matches, startVideoTimeSeconds, videoOffset]);

  // As the video plays, shows each jump's info as playback crosses its
  // moment. Also catches a direct seek that lands right on/just after a jump
  // (e.g. clicking that event in the list).
  useEffect(() => {
    if (jumpEvents.length === 0) return;
    const prev = lastVideoProgressRef.current;
    lastVideoProgressRef.current = videoProgress;
    if (prev == null) return;
    let crossed = null;
    if (videoProgress >= prev) {
      for (const { ev, pos } of jumpEvents) {
        if (pos > prev && pos <= videoProgress) crossed = ev;
      }
    } else {
      // Seeked backward -- show the jump if we landed right on/after it.
      for (const { ev, pos } of jumpEvents) {
        if (pos <= videoProgress && videoProgress - pos <= 3) crossed = ev;
      }
    }
    if (crossed) showJumpOverlay(crossed);
  }, [videoProgress, jumpEvents]);

  const doSelectEvent = (ev) => {
    if (matches.length === 1) {
      setSelectedEvent(ev);
      if (ev.imaHeightM != null) showJumpOverlay(ev);
      const tm = getTimingForEvent(ev);
      console.log("[doSelectEvent] event:", ev);
      console.log("[doSelectEvent] ev.TimeStamp:", ev.TimeStamp, "videoStartTimeSeconds:", startVideoTimeSeconds, "videoOffset:", videoOffset);
      if (ev.VideoPosition !== undefined && ev.VideoPosition !== 0) {
        console.log("[doSelectEvent] videoPosition:", ev.VideoPosition, "leadTime:", tm.leadTime, "seekTo:", ev.VideoPosition - tm.leadTime);
        seekToAndPlay(ev.VideoPosition - tm.leadTime);
      } else if (startVideoTimeSeconds !== null && startVideoTimeSeconds !== '-1' && startVideoTimeSeconds !== -1 && ev.TimeStamp) {
        const secondsSinceEpoch = Math.round(ev.TimeStamp?.getTime() / 1000);
        const offset = videoOffset !== null && videoOffset >= 0 ? videoOffset : 0;
        const loc = secondsSinceEpoch - startVideoTimeSeconds + offset - tm.leadTime;
        console.log("[doSelectEvent] secondsSinceEpoch:", secondsSinceEpoch, "offset:", offset, "leadTime:", tm.leadTime, "seekTo:", loc);
        seekToAndPlay(loc);
      }
    } else {
      setSelectedEvent(ev);
      const match = ev.Drill.match;
      let svts = startVideoTimeSeconds;
      let vos = videoOffset;
      let diffvideo = false;
      if (match !== currentMatch) {
        setCurrentMatch(match);
        const info = resolveMatchVideoInfo(match);
        svts = info.svts;
        vos = info.offset;
        if (info.onlineUrl) {
          const yturl = convertYouTubeUrl(info.onlineUrl);
          setVideoFilePath(toHlsStreamUrl(yturl));
        } else {
          setVideoFilePath(null);
        }
        setIsReady(false);
        setIsPlaying(true);
        diffvideo = true;
      }
      const tm = getTimingForEvent(ev);
      let vt = null;
      if (ev.VideoPosition !== undefined && ev.VideoPosition !== 0) {
        vt = ev.VideoPosition - tm.leadTime;
      } else if (svts !== null && ev.TimeStamp) {
        const secondsSinceEpoch = Math.round(ev.TimeStamp.getTime() / 1000);
        const offset = vos !== null && vos >= 0 ? vos : 0;
        vt = secondsSinceEpoch - svts + offset - tm.leadTime;
      }
      if (diffvideo) {
        setCurrentVideoTime(vt);
      } else {
        seekToAndPlay(vt);
      }
    }
  };

  const handleChange = (e) => {
    setVideoOnlineUrl(e.target.value);
  };

  // True when an event carries no usable timing at all (no DVW time-of-day
  // field and no recorded video position) — these can't anchor a sync.
  const hasUsableTiming = (ev) =>
    (ev.TimeStamp && !isNaN(ev.TimeStamp.getTime())) ||
    (ev.VideoPosition !== undefined && ev.VideoPosition !== 0);

  const describeEvent = (ev) =>
    (ev.Player ? `${ev.Player.shirtNumber}. ${ev.Player.NickName?.toUpperCase()} — ` : "") +
    DVEventString(ev);

  const onSynchVideo = async () => {
    if (selectedEvent === null) {
      toast.error("Please select an event to synch with video!");
      return;
    }
    if (!hasUsableTiming(selectedEvent)) {
      const suggestion = matches[0].events.find(hasUsableTiming);
      toast.error(
        "There is no timestamp for this event, so it cannot be used as the synchronisation anchor. Please select another event." +
          (suggestion ? ` Try: "${describeEvent(suggestion)}".` : "")
      );
      return;
    }
    const secondsSinceEpoch = Math.round(
      selectedEvent.TimeStamp.getTime() / 1000
    );
    setStartVideoTimeSeconds(secondsSinceEpoch);
    // For HLS streams the active player is hlsVideoRef (plain <video>), not
    // the ReactPlayer instance.  Reading from the wrong ref produced an
    // incorrect offset that shifted all seek positions.
    const voffset = videoFilePath?.endsWith(".m3u8")
      ? (hlsVideoRef.current?.currentTime ?? 0)
      : playerRef.current.getCurrentTime();
    setVideoOffset(voffset);
    const prefix =
      selectedEvent.Drill.match.app === "VBStats"
        ? selectedEvent.Drill.match.Guid
        : selectedEvent.Drill.match.dvstring;
    const obj = localStorage.getItem(prefix + "_videoInfo");
    var vobj = null;
    if (obj && obj !== "undefined") {
      vobj = JSON.parse(obj);
      vobj.videoOffset = voffset;
      vobj.startVideoTimeSeconds = secondsSinceEpoch;
    } else {
      vobj = {
        matchDVString: matches[0].dvstring,
        videoOnlineUrl: videoOnlineUrl,
        videoFileName: null,
        videoFileObject: null,
        videoOffset: voffset,
        startVideoTimeSeconds: secondsSinceEpoch,
      };
    }
    localStorage.setItem(prefix + "_videoInfo", JSON.stringify(vobj));
    matches[0].videoOffset = voffset;
    matches[0].videoStartTimeSeconds = secondsSinceEpoch;
    // localStorage.setItem(videoFileName + "_offset", voffset.toString());
    // localStorage.setItem(
    //   videoFileName + "_startVideoTime",
    //   secondsSinceEpoch.toString()
    // );
    // Shift every event's own recorded video position (sm[12] in the DVW scout
    // line) by a constant delta, rather than recomputing it from wall-clock
    // TimeStamp differences — the scout line's own position already accounts
    // for coding pauses/breaks that the wall clock does not, so re-deriving it
    // from TimeStamp arithmetic drifted away from the correct seek position.
    const originalSyncVideoPosition = selectedEvent.VideoPosition;
    if (originalSyncVideoPosition !== undefined && originalSyncVideoPosition !== 0) {
      const positionDelta = voffset - originalSyncVideoPosition;
      for (var ev of matches[0].events) {
        if (ev.VideoPosition !== undefined && ev.VideoPosition !== 0) {
          ev.VideoPosition = ev.VideoPosition + positionDelta;
        }
      }
    } else {
      // No reliable per-event video position to shift (e.g. a live-coded
      // match with no vp recorded yet) — fall back to wall-clock derivation.
      for (var ev of matches[0].events) {
        ev.VideoPosition =
          ev.TimeStamp?.getTime() / 1000 -
          selectedEvent.TimeStamp?.getTime() / 1000 +
          voffset;
      }
    }
    toast.success("Video synched with events!");
    // Persist the new offset/positions the same way showOnlineVideo does —
    // upserts by guid/dvstring, so this also saves never-before-saved matches.
    await onSaveToDatabase();
  };

  const doSaveFile = () => {
    var a = matches[0].buffer.split(/\r?\n/);
    if (a.length == 0) {
      return;
    }
    var fn = "";
    var buffer = "";
    if (matches[0].app === "DataVolley") {
      if (a[0] !== "[3DATAVOLLEYSCOUT]") {
        return;
      }
      var incomments = false;
      for (var nl = 0; nl < a.length; nl++) {
        var s = a[nl];
        if (s.includes("[3COMMENTS]")) {
          incomments = true;
        } else if (incomments && s.includes("[") && s.includes("]")) {
          buffer += "\n";
          if (videoOffset && videoOffset > 0) {
            buffer += ";" + videoOnlineUrl + "\n";
            buffer += ";offset=" + videoOffset + "\n";
            buffer += ";start=" + startVideoTimeSeconds + "\n";
          } else {
            buffer += ";" + videoOnlineUrl + "\n";
          }
          buffer += s;
          incomments = false;
          continue;
        }
        if (incomments) {
          if (s.includes(";http")) continue;
          if (s.includes(";offset")) continue;
          if (s.includes(";start")) continue;
        }
        if (buffer.length > 0) buffer += "\n";
        buffer += s;
      }
    } else if (matches[0].app === "VBStats") {
      if (a[0].includes("PSVB") === false) {
        return;
      }
      var buf = "";
      for (const s of a) {
        if (buf.length > 0) buf += "\n";
        if (s.includes("M~")) {
          var json = s.split("~");
          var m = JSON.parse(json[1]);
          m.videoOnlineUrl = videoOnlineUrl;
          m.videoOffset = videoOffset;
          m.startVideoTimeSeconds = startVideoTimeSeconds;
          buf += "M~" + JSON.stringify(m);
        } else {
          buf += s;
        }
      }
      buffer = myzip(buf);
    }

    fn = makeFilename(matches[0].filename.name, "vblive", "");
    // if (match.filename.name.includes("_vblive")) {
    //   fn = match.filename.name;
    // } else {
    //   const tokens = match.filename.name.split(".");
    //   for (var nn = 0; nn < tokens.length - 1; nn++) {
    //     if (fn.length > 0) fn += ".";
    //     fn += tokens[nn];
    //   }
    //   fn += "_vblive" + "." + tokens[tokens.length - 1];
    // }
    console.log(fn);
    saveToPC(buffer, fn);
  };

  const showOnlineVideo = async () => {
    const vinfo = {
      matchDVString: matches[0].dvstring,
      videoOnlineUrl: videoOnlineUrl,
      videoFileName: null,
      videoFileObject: null,
      videoOffset: null,
      startVideoTimeSeconds: null,
    };
    const prefix =
      matches[0].app === "VBStats" ? matches[0].Guid : matches[0].dvstring;
    localStorage.setItem(prefix + "_videoInfo", JSON.stringify(vinfo));
    setVideoFileObject(null);
    setVideoFileName(null);
    setVideoOffset(null);
    const yturl = convertYouTubeUrl(videoOnlineUrl);
    setVideoFilePath(toHlsStreamUrl(yturl));
    setIsPlaying(true);
    matches[0].videoOnlineUrl = videoOnlineUrl;
    forceUpdate((n) => !n);
    // sessionId is only set for matches opened FROM the database, not for a
    // freshly-imported file that has never been saved — but storeSession
    // upserts keyed by the match's stable guid/dvstring either way, so it's
    // safe (and necessary) to always save here, not just when sessionId exists.
    await onSaveToDatabase();
  };

  const handleVideoUpload = (event) => {
    setIsYoutube(false);
    var url = URL.createObjectURL(event.target.files[0]);
    const vfo = fileObjectToJsonString(event.target.files[0]);
    setVideoFileObject(vfo);
    const vfp = URL.createObjectURL(event.target.files[0]);
    setVideoFilePath(vfp);
    setIsPlaying(true);
    matches[0].videoOnlineUrl = vfp;
    const vfn = event.target.files[0].name;
    setVideoFileName(vfn);
    setVideoOnlineUrl("");
    const vinfo = {
      matchDVString: matches[0].dvstring,
      videoOnlineUrl: null,
      videoFileName: vfn,
      videoFileObject: vfo,
      videoOffset: null,
      startVideoTimeSeconds: null,
    };
    const prefix =
      matches[0].app === "VBStats" ? matches[0].Guid : matches[0].dvstring;
    localStorage.setItem(prefix + "_videoInfo", JSON.stringify(vinfo));
    forceUpdate((n) => !n);
  };

  const handleRadarUpload = (event) => {
    var url = URL.createObjectURL(event.target.files[0]);
    setRadarFileName(event.target.files[0].name);
    const fileReader = new FileReader();
    fileReader.readAsText(event.target.files[0], "UTF-8");
    fileReader.onload = (e) => {
      var matches = readRadarFileData(e.target.result);
      if (matches > 0) {
        toast.success("Import " + matches + " radar readings!");
        setShowRadarFile(false);
        forceUpdate((n) => !n);
      } else {
        toast.error("Unable to import radar readings!");
      }
    };
  };

  const readRadarFileData = (buf) => {
    var rds = [];

    var a = buf.split(/\r?\n/);
    if (a.length === 0) {
      return null;
    }
    for (var nl = 1; nl < a.length; nl++) {
      const line = a[nl];
      var tokens = line.split(",");
      const ts = new Date(tokens[0] + " " + tokens[1]);
      rds.push({
        timestamp: ts,
        activity: tokens[2],
        speed: Number.parseInt(tokens[4]),
        unit: tokens[5],
        index: nl,
      });
    }

    rds = sortBy(rds, "timestamp");

    var serves = [];
    for (var e of matches[0].events) {
      if (e.EventType === 1) {
        e.radar = null;
        serves.push(e);
      }
    }

    var bestmatches = 0;
    var bestmatchesrd = 0;
    for (var startrd = 0; startrd < rds.length; startrd++) {
      const threshold = 3000;
      var matches = 0;
      var nextrd = startrd;
      var firstetime = serves[0].TimeStamp.getTime();
      var firstrdtime = rds[startrd].timestamp.getTime();
      for (var e of serves) {
        const etime = e.TimeStamp.getTime();
        const ediff = etime - firstetime;
        for (var nr = nextrd; nr < rds.length; nr++) {
          const rd = rds[nr];
          const rtime = rd.timestamp.getTime();
          const rdiff = rtime - firstrdtime;
          const diff = Math.abs(rdiff - ediff);
          if (diff <= threshold) {
            e.radar = rd;
            matches++;
            nextrd = nr + 1;
            break;
          }
        }
        if (matches > bestmatches) {
          bestmatches = matches;
          bestmatchesrd = startrd;
        }
      }
      // console.log(startrd, matches);
    }
    // console.log("best:", bestmatchesrd, bestmatches);

    const threshold = 3000;
    matches = 0;
    startrd = bestmatchesrd;
    nextrd = bestmatchesrd;
    firstetime = serves[0].TimeStamp.getTime();
    firstrdtime = rds[startrd].timestamp.getTime();
    for (var e of serves) {
      e.radar = null;
      const etime = e.TimeStamp.getTime();
      const ediff = etime - firstetime;
      for (var nr = nextrd; nr < rds.length; nr++) {
        const rd = rds[nr];
        const rtime = rd.timestamp.getTime();
        const rdiff = rtime - firstrdtime;
        const diff = Math.abs(rdiff - ediff);
        if (diff <= threshold) {
          e.radar = rd;
          matches++;
          nextrd = nr + 1;
          break;
        }
      }
    }
    // console.log(startrd, matches);

    return bestmatches;
  };

  const onGameChanged = (idx) => {
    setSelectedSet(idx);
  };

  const onMatchChanged = (match) => {
    setSelectedMatchGuid(match.guid);
  };

  const onFilter = (fes) => {
    setFilteredEvents(fes);
  };

  const selectAllItems = () => {
    setMenuOpen(false);
    for (var ev of filteredEvents) {
      ev.playlist = true;
    }
    setPlaylistEvents(filteredEvents);
    forceUpdate((n) => !n);
  };

  const unselectAllItems = () => {
    setMenuOpen(false);
    for (var ev of filteredEvents) {
      ev.playlist = false;
    }
    setPlaylistEvents([]);
    forceUpdate((n) => !n);
  };

  const createPlaylist = () => {
    setMenuOpen(false);
    for (var ev of filteredEvents) {
      if (ev.playlist) {
        if (!ev.Drill.match.videoOnlineUrl) {
          toast.error(
            "Play lists can only be created with events that have video online."
          );
          return;
        }
      }
    }

    // if (videoOnlineUrl === null || videoOnlineUrl.length === 0) {
    //   toast.error("Play lists can only be created with an online video.");
    //   return;
    // }
    var evs = [];
    for (var ev of filteredEvents) {
      if (ev.playlist) {
        var loc = 0;
        const tm = getTimingForEvent(ev);
        const matchSvts = ev.Drill.match.videoStartTimeSeconds ?? null;
        const matchVoff = ev.Drill.match.videoOffset ?? null;
        const rawTs = ev.TimeStamp ? Math.round(ev.TimeStamp.getTime() / 1000) : null;
        const rawVidPos = (ev.VideoPosition !== undefined && ev.VideoPosition !== 0)
          ? ev.VideoPosition
          : null;
        if (
          matchSvts != null &&
          matchVoff != null &&
          matchVoff >= 0 &&
          rawTs != null
        ) {
          loc = rawTs - matchSvts + matchVoff - tm.leadTime;
        } else if (rawVidPos != null) {
          loc = rawVidPos - tm.leadTime;
        }

        var xx = "";
        var col = "";
        if (ev.DVGrade === undefined) {
          const ss = getEventInfo(ev);
          xx = ss.sub;
          col = ss.subcolor;
        } else {
          xx = DVEventString(ev);
          col = getEventStringColor(ev);
        }
        const evx = {
          playerName: ev.Player
            ? ev.Player.shirtNumber + ". " + ev.Player.NickName.toUpperCase()
            : "",
          eventString: xx,
          eventStringColor: col,
          eventSubstring:
            t("Game") +
            " " +
            ev.Drill.GameNumber +
            " " +
            ev.TeamScore +
            "-" +
            ev.OppositionScore +
            " R" +
            ev.Row,
          videoOnlineUrl: ev.Drill.match.videoOnlineUrl,
          videoPosition: loc,
          // Raw fields for dynamic position re-computation in Playlist playback
          eventType: ev.EventType,
          timeStampSeconds: rawTs,
          videoStartTimeSeconds: matchSvts,
          videoOffset: matchVoff,
          rawVideoPosition: rawVidPos,
          eventId: ev.EventId,
          matchVideo: ev.Drill.match.videoOnlineUrl
            ? ev.Drill.match.videoOnlineUrl
            : "",
          comment: ev.comment || "",
        };
        evs.push(evx);
      }
    }
    if (evs.length === 0) {
      toast.error("Play list is empty.");
    } else {
      setPlaylistEvents(evs);
      const evstr = JSON.stringify(evs);
      localStorage.setItem("VBLivePlaylistEvents", evstr);
      const pl = {
        events: evs,
      };
      const fn = makeFilename("playlist", "vblive", "playlist");
      const buffer = JSON.stringify(pl);

      // Auto-populate description based on skill, player, team and rotation.
      const playlistEvs = (filteredEvents ?? []).filter((e) => e.playlist);

      // Same skill?
      const uniqueTypes = [...new Set(playlistEvs.map((e) => e.EventType).filter((v) => v !== undefined && v !== null))];
      const sameSkill = uniqueTypes.length === 1;
      const skillLabel = sameSkill ? (eventTypes.find((et) => et.value === uniqueTypes[0])?.label ?? "") : "";

      // Same player?
      const uniquePlayers = [...new Set(evs.map((e) => e.playerName).filter(Boolean))];
      const samePlayer = uniquePlayers.length === 1;

      // Same team?
      const isHomeEv = (ev) => ev.dvString !== undefined ? ev.dvString[0] === "*" : ev.homePlayer === true;
      const getEvTeam = (ev) => isHomeEv(ev) ? ev.Drill?.match?.teamA?.Name : ev.Drill?.match?.teamB?.Name;
      const uniqueTeams = [...new Set(playlistEvs.map(getEvTeam).filter(Boolean))];
      const sameTeam = uniqueTeams.length === 1;

      // Same rotation?
      const uniqueRows = [...new Set(playlistEvs.map((e) => e.Row).filter((v) => v !== undefined && v !== null && v > 0))];
      const sameRotation = uniqueRows.length === 1;
      const rotLabels = getRotationLabels();
      const rotationSuffix = sameRotation ? ` ${rotLabels[uniqueRows[0] - 1] ?? "R" + uniqueRows[0]}` : "";

      let autoDescription = "";
      if (sameSkill) {
        if (samePlayer) {
          autoDescription = `${uniquePlayers[0]} - ${skillLabel}`;
        } else if (sameTeam) {
          autoDescription = `${uniqueTeams[0]} - ${skillLabel}`;
        } else {
          autoDescription = skillLabel;
        }
      } else if (samePlayer) {
        autoDescription = uniquePlayers[0];
      } else if (sameTeam) {
        autoDescription = uniqueTeams[0];
      }
      autoDescription += " -" + rotationSuffix;

      const st = {
        playlistFileData: buffer,
        filename: null,
        playlists: null,
        serverName: currentUser.email,
        description: autoDescription || null,
      };
      navigate("/playlist", { state: st });

      // saveToPC(buffer, fn);
    }
  };

  const doTiming = () => {
    setShowTimingModal(true);
  };

  const doCloseTiming = () => {
    setShowTimingModal(false);
    const obj = localStorage.getItem("vblive_timings");
    if (obj !== null) {
      const vobj = JSON.parse(obj);
      const lts = vobj.leadTimes.split(",");
      setLeadTimes(lts);
      const ds = vobj.durations.split(",");
      setDurations(ds);
    }
  };

  const convertYouTubeUrl = (str) => {
    if (!str) return str;
    if (str.includes("youtu.be") === false) return str;
    const regex = /.*\/([0-9a-zA-Z-\_]*)/gm;
    let m;
    while ((m = regex.exec(str)) !== null) {
      // This is necessary to avoid infinite loops with zero-width matches
      if (m.index === regex.lastIndex) {
        regex.lastIndex++;
      }

      // The result can be accessed through the `m`-variable.
      m.forEach((match, groupIndex) => {
        console.log(`Found match, group ${groupIndex}: ${match}`);
      });
      if (m[1] !== null) {
        setIsYoutube(true);
        return "https://www.youtube.com/watch?v=" + m[1];
      }
    }
    setIsYoutube(false);
    return str;
  };

  useLayoutEffect(() => {
    if (wref.current) {
      const ch = wref.current.offsetHeight - 162;
      console.log("contentsHeight:" + ch);
      setContentsHeight(ch);
    }
    const sb = window.innerWidth > 960 ? 320 + 80 : 80; // smaller than md
    setVideoWidth(window.innerWidth - sb);
    setWindowHeight(window.innerHeight);
  }, []);

  // A multi-match report's OTHER matches (anything besides whichever one was
  // most recently opened individually) often never got videoOnlineUrl saved
  // directly onto the match object -- fall back to that match's own cached
  // localStorage "_videoInfo" entry, same as VideoAnalysisFiltersPage.jsx's
  // loadMatchVideo, so every match's video (not just one) can play back.
  const resolveMatchVideoInfo = (match) => {
    let onlineUrl = match.videoOnlineUrl;
    let offset = match.videoOffset;
    let svts = match.videoStartTimeSeconds;
    if (!onlineUrl) {
      try {
        const prefix = match.app === "VBStats" ? match.Guid : match.dvstring;
        const vobj = localStorage.getItem(prefix + "_videoInfo");
        if (vobj) {
          const vinfo = JSON.parse(vobj);
          if (vinfo?.matchDVString === match.dvstring && vinfo.videoOnlineUrl) {
            onlineUrl = vinfo.videoOnlineUrl;
            offset = vinfo.videoOffset ?? offset;
            svts = vinfo.startVideoTimeSeconds ?? svts;
            // Only ever cached in localStorage, never saved onto the match
            // itself -- write it back and persist silently so every OTHER
            // feature reading match.videoOnlineUrl directly (makePlaylist(),
            // Catapult's Watch Video, etc.) sees it too, not just this page.
            match.videoOnlineUrl = onlineUrl;
            if (offset != null) match.videoOffset = offset;
            if (svts != null) match.videoStartTimeSeconds = svts;
            if (currentUser?.email) {
              storeSession(match, currentUser).catch((e) =>
                console.error("Failed to save recovered video info", e)
              );
            }
          }
        }
      } catch {
        /* no cached video info for this match */
      }
    }
    return { onlineUrl, offset, svts };
  };

  useEffect(() => {
    if (matches.length > 1) {
      const first = matches[0];
      setCurrentMatch(first);
      const info = resolveMatchVideoInfo(first);
      setVideoOffset(info.offset != null && info.offset >= 0 ? info.offset : -1);
      if (info.svts != null) setStartVideoTimeSeconds(info.svts);
      if (info.onlineUrl) {
        const yturl = convertYouTubeUrl(info.onlineUrl);
        setVideoOnlineUrl(yturl);
        setVideoFilePath(toHlsStreamUrl(yturl));
      }
    } else if (matches.length === 1) {
      if (matches[0].videoOnlineUrl) {
        const yturl = convertYouTubeUrl(matches[0].videoOnlineUrl);
        setVideoOnlineUrl(yturl);
        setVideoFilePath(toHlsStreamUrl(yturl));
        setVideoOffset(matches[0].videoOffset ? matches[0].videoOffset : -1);
        if (matches[0].videoStartTimeSeconds != null) {
          setStartVideoTimeSeconds(matches[0].videoStartTimeSeconds);
        }
      } else {
        const prefix =
          matches[0].app === "VBStats" ? matches[0].Guid : matches[0].dvstring;
        const vobj = localStorage.getItem(prefix + "_videoInfo");
        if (vobj) {
          const vinfo = JSON.parse(vobj);
          setVideoInfo(vobj);
          if (vinfo?.matchDVString === matches[0].dvstring) {
            setVideoOffset(vinfo.videoOffset);
            setStartVideoTimeSeconds(vinfo.startVideoTimeSeconds);
            if (vinfo.videoOnlineUrl !== null) {
              const yturl = convertYouTubeUrl(vinfo.videoOnlineUrl);
              setVideoOnlineUrl(yturl);
              setVideoFilePath(toHlsStreamUrl(yturl));
              // This video was only ever cached in localStorage (never saved
              // onto the match itself), so it plays fine here but every
              // OTHER feature reading match.videoOnlineUrl directly (e.g.
              // makePlaylist(), Catapult's Watch Video) sees nothing. Write
              // it back onto the match and persist silently so they stay in
              // sync with what this page already shows.
              matches[0].videoOnlineUrl = vinfo.videoOnlineUrl;
              if (vinfo.videoOffset != null) matches[0].videoOffset = vinfo.videoOffset;
              if (vinfo.startVideoTimeSeconds != null) {
                matches[0].videoStartTimeSeconds = vinfo.startVideoTimeSeconds;
              }
              if (currentUser?.email) {
                storeSession(matches[0], currentUser).catch((e) =>
                  console.error("Failed to save recovered video info", e)
                );
              }
            } else if (vinfo.videoFileObject !== null) {
              const fobj = JSON.parse(vinfo.videoFileObject);
              // const vfp = URL.createObjectURL(fobj)
              // setVideoFilePath(vfp)
              setVideoFileName(fobj.name);
            }
          }
        } else {
          if (matches[0].videoUrl !== undefined) {
            const yturl = convertYouTubeUrl(matches[0].videoUrl);
            setVideoOnlineUrl(yturl);
            setVideoFilePath(toHlsStreamUrl(yturl));
          }
        }
      }
    }
    var fevents = [];
    var natcbs = 0;
    var atcbs = [{ value: 0, label: t("All Combos") }];
    var selatcbs = [{ value: 0, label: t("All Combos") }];
    var plas = [{ value: 0, label: t("All Players") }];
    var plbs = [{ value: 0, label: t("All Players") }];
    var nscs = 0;
    var scs = [{ value: 0, label: t("All Calls") }];
    var selscs = [{ value: 0, label: t("All Calls") }];
    var gms = [{ value: 0, label: t("All Sets") }];

    for (var match of matches) {
      const tma = team === match.teamA.Name ? match.teamA : match.teamB;
      const tmb = team === match.teamA.Name ? match.teamB : match.teamA;
      for (var xpl of tma.players) {
        if (
          plas.filter(
            (pl) =>
              pl.FirstName + "_" + pl.LastName ===
              xpl.FirstName + "_" + xpl.LastName
          ).length === 0
        ) {
          const plname =
            xpl.shirtNumber +
            ". " +
            xpl.FirstName +
            " " +
            xpl.LastName.toUpperCase();
          plas.push({ value: plas.length, label: plname, guid: plname });
        }
      }
      for (var xpl of tmb.players) {
        if (
          plbs.filter(
            (pl) =>
              pl.FirstName + "_" + pl.LastName ===
              xpl.FirstName + "_" + xpl.LastName
          ).length === 0
        ) {
          const plname =
            xpl.shirtNumber +
            ". " +
            xpl.FirstName +
            " " +
            xpl.LastName.toUpperCase();
          plbs.push({ value: plas.length, label: plname, guid: plname });
        }
      }
      setTeamAPlayers(plas);
      setTeamBPlayers(plbs);

      if (match.attackCombos !== undefined) {
        for (const atcb of match.attackCombos) {
          if (atcbs.filter((at) => at.label === atcb.code).length === 0) {
            natcbs++;
            const xx = { value: natcbs, label: atcb.code, name: atcb.name };
            atcbs.push(xx);
          }
        }
      }

      if (match.setterCalls !== undefined) {
        for (const sc of match.setterCalls) {
          if (scs.filter((at) => at.label === sc.code).length === 0) {
            nscs++;
            const xx = { value: nscs, label: sc.code, name: sc.name };
            scs.push(xx);
          }
        }
      }

      for (var ng = 1; ng <= match.sets.length; ng++) {
        if (gms.filter((at) => at.value === ng).length === 0) {
          gms.push({ value: ng, label: t("Game") + " " + ng });
        }
      }

      for (var game of match.sets) {
        fevents.push(...game.events);
      }

      const playlistevstr = localStorage.getItem("VBLivePlaylistEvents");
      var plevs = [];
      if (playlistevstr !== null) {
        const evs = JSON.parse(playlistevstr);
        for (var evx of evs) {
          const ff = fevents.filter(
            (fev) =>
              fev.EventId === evx.eventId &&
              fev.Drill.match.videoOnlineUrl === evx.videoOnlineUrl
          );
          if (ff.length === 1) {
            ff[0].playlist = true;
            plevs.push(ff[0]);
          }
        }
      }
    }

    setPlaylistEvents(plevs);
    setFilteredEvents(fevents);
    setAttackCombos(atcbs);
    setSelectedAttackCombos(selatcbs);
    setSetterCalls(scs);
    setSelectedSetterCalls(selscs);
    setGames(gms);
    setSelectedGames([{ value: 0, label: t("All Sets") }]);
    const sins = [1, 6, 5, 4, 3, 2];
    var rts = [{ value: 0, label: t("All Rotations") }];
    for (var nr = 1; nr <= 6; nr++) {
      rts.push({
        value: nr,
        label: "R" + nr + " - Setter in " + sins[nr - 1],
      });
    }
    setRotations(rts);
    setSelectedRotations([{ value: 0, label: t("All Rotations") }]);
    setSelectedTeamAPlayers([{ value: 0, label: t("All Players") }]);
    setSelectedTeamBPlayers([{ value: 0, label: t("All Players") }]);
    forceUpdate((n) => !n);

    function handleWindowResize() {
      if (wref.current) {
        const ch = wref.current.offsetHeight - 162;
        console.log("contentsHeight:" + ch);
        setContentsHeight(ch);
      }
      const sb = window.innerWidth > 960 ? 320 + 80 : 80; // smaller than md
      setVideoWidth(window.innerWidth - sb);
      setWindowHeight(window.innerHeight);
      setWindowHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("resize", handleWindowResize);
    };
  }, []);

  useEffect(() => {
    setSelectedSet(selectedGame);
    forceUpdate((n) => !n);
  }, [selectedGame, isCollapseAll]);

  useEffect(() => {}, [selectedSet, selectedMatchGuid]);

  useEffect(() => {
    // Bind the event listener
    document.addEventListener("mousedown", handleOutsideClicks);
    return () => {
      // Unbind the event listener on clean up
      document.removeEventListener("mousedown", handleOutsideClicks);
    };
  }, [menuOpen]);

  // Re-fetch and re-parse live session data, then APPEND only new events.
  // Existing event objects and all EventsList UI state (collapse, scroll, selection)
  // are preserved because we never replace data that is already present.
  const refreshLiveData = useCallback(async () => {
    if (!sessionId) return;
    try {
      const sessionData = await getSession(sessionId);
      if (!sessionData || !sessionData.stats) return;

      // Check whether the session is still live (updated within the last 5 min).
      if (sessionData.utcLastUpdate) {
        const now = new Date();
        const utcNow = Date.UTC(
          now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(),
          now.getUTCHours(), now.getUTCMinutes(), now.getUTCSeconds(), now.getUTCMilliseconds()
        );
        const stillLive = utcNow / 1000 - sessionData.utcLastUpdate < 300;
        setIsLive(stillLive);
        if (!stillLive) return;
      }

      let appnm = matches[0].app;
      let mx = null;
      if (appnm === "DVMate") {
        mx = initWithDVWCompressedBuffer(sessionData.stats);
        if (!mx) { mx = initWithPSVBCompressedBuffer(sessionData.stats); appnm = "VBStats"; }
      } else {
        mx = initWithPSVBCompressedBuffer(sessionData.stats);
        appnm = "VBStats";
      }
      if (!mx) return;

      if (appnm === "VBStats") {
        const latestData = await getLatestStats(sessionId, 0);
        mx = parseLatestPSVBStats(latestData, mx);
        mx = calculatePSVBStats(mx);
      } else {
        mx = calculateDVWStats(mx);
      }
      mx.app = appnm;
      mx = calculateSideoutStats(mx, appnm);
      // Preserve video sync settings from the current match.
      mx.videoOnlineUrl = matches[0].videoOnlineUrl;
      mx.videoStartTimeSeconds = matches[0].videoStartTimeSeconds;
      mx.videoOffset = matches[0].videoOffset;

      // Stable dedup key that works for both DVW (has deterministic EventId after
      // the generateMatch reset fix) and VBStats (no EventId).
      // We prefer EventId when it looks valid, but always mix in a content-based
      // fallback so that stale EventId = "0" or undefined never causes collisions.
      const eventKey = (ev) => {
        const ts = ev.TimeStamp instanceof Date ? ev.TimeStamp.getTime() : 0;
        const gn = ev.Drill?.GameNumber ?? 0;
        const hs = ev.TeamScore ?? 0;
        const as = ev.OppositionScore ?? 0;
        const et = ev.EventType ?? 0;
        const base = `${gn}-${hs}-${as}-${et}-${ts}`;
        // Tack on EventId only when it's a non-trivial value so we don't lose
        // the deterministic DVW index when it IS available.
        if (ev.EventId != null && ev.EventId !== "" && ev.EventId !== "0") {
          return `${ev.EventId}:${base}`;
        }
        return base;
      };

      // Build the known-keys set lazily on the first refresh.
      if (!knownEventIdsRef.current) {
        knownEventIdsRef.current = new Set(
          matches[0].sets.flatMap(s => s.events.map(eventKey))
        );
      }

      const appendedEvents = [];
      let hasNewSets = false;
      let scoresChanged = false;

      for (const newSet of mx.sets) {
        const existingSet = matches[0].sets.find(s => s.GameNumber === newSet.GameNumber);
        if (!existingSet) {
          // Entirely new set — append it and record all its event keys.
          matches[0].sets.push(newSet);
          // filteredEvents defaults to the full events array until EventsList filters.
          newSet.filteredEvents = newSet.events;
          newSet.events.forEach(e => knownEventIdsRef.current.add(eventKey(e)));
          appendedEvents.push(...newSet.events);
          hasNewSets = true;
        } else {
          for (const ev of newSet.events) {
            const key = eventKey(ev);
            if (!knownEventIdsRef.current.has(key)) {
              existingSet.events.push(ev);
              knownEventIdsRef.current.add(key);
              appendedEvents.push(ev);
            }
          }
          // Sync the freshly re-parsed drill score onto the persisted set on
          // every refresh (not just when new events arrive). DVWWriter emits a
          // trailing corrective `*p<pointsA>:<pointsB>` marker for every set
          // (finished or still in progress) reflecting the CURRENT live
          // score, and DVWFile.js applies any `*p` marker straight onto the
          // drill's HomeScore/AwayScore — so `newSet.HomeScore/AwayScore` is
          // always accurate on a fresh full re-parse. Without this the
          // EventsList "Game N" header stayed stuck at whatever score was
          // read on the very first load.
          if (
            existingSet.HomeScore !== newSet.HomeScore ||
            existingSet.AwayScore !== newSet.AwayScore
          ) {
            existingSet.HomeScore = newSet.HomeScore;
            existingSet.AwayScore = newSet.AwayScore;
            scoresChanged = true;
          }
        }
      }

      // Keep match-level flat events list in sync.
      matches[0].events = matches[0].sets.flatMap(s => s.events);

      if (appendedEvents.length > 0 || hasNewSets || scoresChanged) {
        // Extend filteredEvents state (used for playlist building).
        setFilteredEvents(prev => [...prev, ...appendedEvents]);

        // Always give `currentMatches` a new top-level reference so EventsList's
        // `useEffect(() => doFilters(), [filters, matches])` re-fires and
        // recomputes `s.filteredEvents` for every set from the canonical
        // `s.events` against the CURRENT filter criteria. Previously, when a
        // filter was active, newly-appended live events were pushed directly
        // into `s.filteredEvents` unconditionally (bypassing the filter
        // entirely), so live updates always showed regardless of the active
        // filter selection. Re-running doFilters() is cheap (an in-memory
        // array scan) and doesn't disturb collapse/scroll/selection state,
        // which are driven by separate effects (`[collapseAll]`, etc).
        setCurrentMatches(prev => [...prev]);
      }
    } catch (e) {
      console.error("[VideoAnalysis] refreshLiveData error:", e);
    }
    liveCountRef.current = 0;
    setLiveCounter(0);
  }, [sessionId]);

  // One-second countdown scheduler — mirrors Session.jsx schedule().
  useEffect(() => {
    if (!isLive) return;
    const timerId = setTimeout(() => {
      const next = liveCounter + 1;
      liveCountRef.current = next;
      if (liveCounter >= liveRefreshInterval - 1) {
        if (liveAutoRefresh) refreshLiveData();
        setLiveCounter(0);
      } else {
        setLiveCounter(next);
      }
    }, 1000);
    return () => clearTimeout(timerId);
  }, [liveCounter, isLive, liveAutoRefresh, refreshLiveData]);

  const doFilters = () => {
    setAllFilters({
      teamAPlayers: selectedTeamAPlayers,
      teamBPlayers: selectedTeamBPlayers,
      eventTypes: selectedEventTypes,
      eventResults: selectedEventResults,
      attackTypes: selectedAttackTypes,
      hitTypes: selectedHitTypes,
      blockTypes: selectedBlockTypes,
      attackCombos: selectedAttackCombos,
      setterCalls: selectedSetterCalls,
      games: selectedGames,
      rotations: selectedRotations,
      powerPlay: selectedPowerPlay,
    });
    setShowFiltersModal(true);
  };

  //create a function in your component to handleOutsideClicks
  const handleOutsideClicks = (event) => {
    if (
      menuOpen &&
      menuRef.current &&
      !menuRef.current.contains(event.target)
    ) {
      setMenuOpen(false);
    }
  };

  const drawerSideClass = () => {
    // return "flex-col w-[40vw] h-[" + contentsHeight + "px] bg-green-300"
    return (
      "flex-col px-2 h-[" + contentsHeight + "px] overflow-y-auto bg-purple-500"
    );
  };

  const onBackClick = () => {
    navigate(-1);
  };

  const calcVideoSize = () => {
    var maxh = windowHeight - 300;
    var w = videoWidth;
    var h = (videoWidth * 9) / 16;
    if (h > maxh) {
      h = maxh;
      w = (h * 16) / 9;
    }
    return { width: w, height: h };
  };

  const videoTitle = () => {
    var title = "";
    if (matches.length === 1) {
      title =
        matches[0].teamA.Name.toUpperCase() +
        " vs " +
        matches[0].teamB.Name.toUpperCase();
    } else if (selectedEvent) {
      const m = selectedEvent.Drill.match;
      title = m.teamA.Name.toUpperCase() + " vs " + m.teamB.Name.toUpperCase();
    }
    return title;
  };

  const doLeftSide = () => {
    return (
      <div className="flex-col min-w-[320px] border-r border-base-content/10">
        <div className="flex justify-between gap-1 h-10 p-1 bg-base-300 mt-1">
          <div className="flex gap-1">
            <a data-tooltip-id="tt-filters" data-tooltip-content={t("Filters")}>
              <FunnelIcon className="btn-toolbar" onClick={() => doFilters()} />
            </a>
            <Tooltip
              id="tt-filters"
              place={"bottom-end"}
              style={{
                backgroundColor: "hsl(var(--b3))",
                color: "hsl(var(--bc))",
              }}
            />
            <a
              data-tooltip-id="tt-filterspage"
              data-tooltip-content={t("Advanced Filters")}
            >
              <AdjustmentsHorizontalIcon
                className="btn-toolbar"
                onClick={() =>
                  navigate("/videoanalysisfilters", { state: { matches, team } })
                }
              />
            </a>
            <Tooltip
              id="tt-filterspage"
              place={"bottom-end"}
              style={{
                backgroundColor: "hsl(var(--b3))",
                color: "hsl(var(--bc))",
              }}
            />
            {matches.length === 1 ? (
              <div className="dropdown">
                <a data-tooltip-id="tt-filters" data-tooltip-content={t("Sets")}>
                  <ListBulletIcon tabIndex={0} className="btn-toolbar" />
                </a>
                <Tooltip
                  id="tt-filters"
                  place={"bottom-end"}
                  style={{
                    backgroundColor: "hsl(var(--b3))",
                    opacity: 0.9,
                    color: "hsl(var(--bc))",
                  }}
                />
                <ul
                  tabIndex={0}
                  className="dropdown-content menu p-2 shadow bg-base-100 w-52"
                >
                  {matches[0].sets.map((vobj, idx) => (
                    <li key={idx} onClick={() => onGameChanged(idx + 1)}>
                      <a>{t("Game")} {vobj.GameNumber}</a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="dropdown">
                <a data-tooltip-id="tt-filters" data-tooltip-content={t("Sets")}>
                  <ListBulletIcon tabIndex={0} className="btn-toolbar" />
                </a>
                <Tooltip
                  id="tt-filters"
                  place={"bottom-end"}
                  style={{
                    backgroundColor: "hsl(var(--b3))",
                    opacity: 0.9,
                    color: "hsl(var(--bc))",
                  }}
                />
                <ul
                  tabIndex={0}
                  className="dropdown-content menu p-2 shadow bg-base-100 w-52"
                >
                  {matches.map((match, idx) => (
                    <li key={idx} onClick={() => onMatchChanged(match)}>
                      <a>
                        {match.teamA.Name} vs {match.teamB.Name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="dropdown" ref={menuRef}>
              <div tabIndex={0} onClick={() => setMenuOpen(!menuOpen)}>
                <a
                  data-tooltip-id="tt-filters"
                  data-tooltip-content={t("Playlists")}
                >
                  <VideoCameraIcon className="btn-toolbar" />
                </a>
                <Tooltip
                  id="tt-filters"
                  place={"bottom-end"}
                  style={{
                    backgroundColor: "hsl(var(--b3))",
                    opacity: 0.9,
                    color: "hsl(var(--bc))",
                  }}
                />
              </div>
              {menuOpen ? (
                <ul
                  tabIndex={0}
                  className="dropdown-content menu p-2 shadow bg-base-100 w-52"
                >
                  <li onClick={() => selectAllItems()}>
                    <a>{t("Select all items")}</a>
                  </li>
                  <li onClick={() => unselectAllItems()}>
                    <a>{t("Unselect all items")}</a>
                  </li>
                  <li onClick={() => createPlaylist()}>
                    <a>{t("Create play list")}</a>
                  </li>
                </ul>
              ) : (
                <></>
              )}
            </div>
          </div>
          <div>
            {isCollapseAll ? (
              <>
                <a
                  data-tooltip-id="tt-filters"
                  data-tooltip-content={t("Expand All")}
                >
                  <ChevronDoubleDownIcon
                    className="btn-toolbar"
                    onClick={() => setIsCollapseAll(false)}
                  />
                </a>

                <Tooltip
                  id="tt-filters"
                  place={"bottom-end"}
                  style={{
                    backgroundColor: "hsl(var(--b3))",
                    opacity: 0.9,
                    color: "hsl(var(--bc))",
                  }}
                />
              </>
            ) : (
              <>
                <a
                  data-tooltip-id="tt-filters"
                  data-tooltip-content={t("Collapse All")}
                >
                  <ChevronDoubleUpIcon
                    className="btn-toolbar"
                    onClick={() => setIsCollapseAll(true)}
                  />
                </a>

                <Tooltip
                  id="tt-filters"
                  place={"bottom-end"}
                  style={{
                    backgroundColor: "hsl(var(--b3))",
                    opacity: 0.9,
                    color: "hsl(var(--bc))",
                  }}
                />
              </>
            )}
          </div>

          <div>
            {matches.length === 1 ? (
              <>
                <a
                  data-tooltip-id="tt-filters"
                  data-tooltip-content={t("Import Radar File")}
                >
                  <SignalIcon
                    className="btn-toolbar"
                    onClick={() => setShowRadarFile(!showRadarFile)}
                  />
                </a>

                <Tooltip
                  id="tt-filters"
                  place={"bottom-end"}
                  style={{
                    backgroundColor: "hsl(var(--b3))",
                    opacity: 0.9,
                    color: "hsl(var(--bc))",
                  }}
                />
              </>
            ) : (
              <></>
            )}
          </div>
        </div>
        <div className="overflow-y-auto h-[calc(100vh-40px)]">
          <div className="">
            <EventsList
              matches={currentMatches}
              team={team}
              filters={allFilters}
              selectedSet={selectedSet}
              selectedMatchGuid={selectedMatchGuid}
              doSelectEvent={(ev) => doSelectEvent(ev)}
              onFilter={(fes) => onFilter(fes)}
              collapseAll={isCollapseAll}
            />
          </div>
        </div>
      </div>
    );
  };

  const doRightSide = () => {
    return (
      <div className="flex-col w-full">
        <div className="flex flex-col w-full justify-between">
          <div>
            {showRadarFile === false ? (
              <></>
            ) : (
              <div className="flex my-2">
                <input
                  type="file"
                  id="selectedRadarFile"
                  ref={radarRef}
                  style={{ display: "none" }}
                  onChange={handleRadarUpload}
                  onClick={(event) => {
                    event.target.value = null;
                  }}
                />
                <input
                  type="button"
                  className="btn-in-form w-44"
                  value="Select Radar File..."
                  onClick={() =>
                    document.getElementById("selectedRadarFile").click()
                  }
                />
                <label className="label ml-4">
                  <span className="label-text text-base-content/50">
                    {videoFileName === null
                      ? "radar file not selected"
                      : radarFileName}
                  </span>
                </label>
              </div>
            )}
            {matches.length === 1 ? (
              <div className="flex flex-col">
                <div className="flex justify-between">
                  <div className="flex my-2">
                    <input
                      type="file"
                      id="selectedVideoFile"
                      ref={dvRef}
                      style={{ display: "none" }}
                      onChange={handleVideoUpload}
                      onClick={(event) => {
                        event.target.value = null;
                      }}
                    />
                    <a
                      data-tooltip-id="tt-videofile"
                      data-tooltip-content={t("Select local video file")}
                    >
                      <FilmIcon
                        className="btn-toolbar"
                        onClick={() =>
                          document.getElementById("selectedVideoFile").click()
                        }
                      />
                    </a>
                    <Tooltip
                      id="tt-videofile"
                      place={"bottom-start"}
                      style={{
                        backgroundColor: "hsl(var(--b3))",
                        color: "hsl(var(--bc))",
                      }}
                    />

                    <label className="label ml-4">
                      <span className="label-text text-base-content/50">
                        {videoFileName === null
                          ? "..."
                          : videoFileName}
                      </span>
                    </label>
                  </div>
                  <div className="flex gap-1">
                    <a
                      data-tooltip-id="tt-save"
                      data-tooltip-content={t("Save to database")}
                    >
                      <CloudArrowUpIcon
                        className="btn-toolbar mt-2"
                        onClick={() => onSaveToDatabase()}
                      />
                    </a>
                    <Tooltip
                      id="tt-save"
                      place={"bottom-end"}
                      style={{
                        backgroundColor: "hsl(var(--b3))",
                        color: "hsl(var(--bc))",
                      }}
                    />
                  </div>
                </div>
                <div className="flex gap-1 mb-2">
                  <input
                    type="text"
                    className="w-full text-base-content/50 bg-base-100/50 input input-sm rounded-md"
                    id="onlineVideoUrl"
                    value={videoOnlineUrl}
                    placeholder="Online video URL"
                    onChange={handleChange}
                  />
                  <a
                    data-tooltip-id="tt-save"
                    data-tooltip-content={t("Load and play online video")}
                  >
                    <PlayIcon
                      className="btn-toolbar"
                      onClick={() => showOnlineVideo()}
                    />
                  </a>
                  <Tooltip
                    id="tt-save"
                    place={"bottom-end"}
                    style={{
                      backgroundColor: "hsl(var(--b3))",
                      color: "hsl(var(--bc))",
                    }}
                  />
                  <a
                    data-tooltip-id="tt-save"
                    data-tooltip-content={t("Synchronise video with events")}
                  >
                    <ArrowPathRoundedSquareIcon
                      className="btn-toolbar"
                      onClick={() => onSynchVideo()}
                    />
                  </a>
                  <Tooltip
                    id="tt-save"
                    place={"bottom-end"}
                    style={{
                      backgroundColor: "hsl(var(--b3))",
                      color: "hsl(var(--bc))",
                    }}
                  />
                </div>
              </div>
            ) : (
              <></>
            )}
            <div
              className="relative flex m-2 p-1 justify-center bg-black"
              onTouchStart={onTouchStart}
              onTouchMove={onTouchMove}
              onTouchEnd={onTouchEnd}
            >
              {jumpOverlayEvent && jumpOverlayEvent.imaHeightM != null && (
                <div className="absolute top-2 left-1/2 z-10 -translate-x-1/2 pointer-events-none rounded bg-black/60 px-4 py-2 text-center leading-tight text-white">
                  <div className="text-base font-extrabold uppercase">
                    {jumpOverlayEvent.Player?.NickName ||
                      jumpOverlayEvent.Player?.LastName ||
                      ""}
                    {jumpOverlayEvent.EventString?.trim() &&
                      ` - ${jumpOverlayEvent.EventString.trim()}`}
                  </div>
                  <div className="text-sm font-extrabold">
                    {formatJumpHeightM(jumpOverlayEvent.imaHeightM)}
                    {jumpOverlayEvent.imaDurationSec != null &&
                      ` - ${jumpOverlayEvent.imaDurationSec.toFixed(2)} s`}
                  </div>
                </div>
              )}
              {isYoutube ? (
                <>
                  <ReactPlayer
                    ref={playerRef}
                    url={videoFilePath}
                    playing={isPlaying}
                    controls={true}
                    onReady={() => playerReady()}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onProgress={(state) => setVideoProgress(state.playedSeconds)}
                    progressInterval={500}
                    onTouchStart={onTouchStart}
                    onTouchMove={onTouchMove}
                    onTouchEnd={onTouchEnd}
                    width={`${calcVideoSize().width}px`}
                    height={`${calcVideoSize().height}px`}
                    config={{ file: { attributes: { crossOrigin: "anonymous" } } }}
                  />
                </>
              ) : videoFilePath?.endsWith(".m3u8") ? (
                <video
                  ref={hlsVideoRef}
                  controls
                  onCanPlay={() => hlsReady()}
                  onTimeUpdate={() => setVideoProgress(hlsVideoRef.current?.currentTime ?? 0)}
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                  width={`${calcVideoSize().width}px`}
                  height={`${calcVideoSize().height}px`}
                  crossOrigin="anonymous"
                />
              ) : (
                <ReactPlayer
                  ref={playerRef}
                  url={videoFilePath}
                  playing={isPlaying}
                  controls={true}
                  onReady={() => playerReady()}
                  onPlay={() => setIsPlaying(true)}
                  onPause={() => setIsPlaying(false)}
                  onProgress={(state) => setVideoProgress(state.playedSeconds)}
                  progressInterval={500}
                  onTouchStart={onTouchStart}
                  onTouchMove={onTouchMove}
                  onTouchEnd={onTouchEnd}
                  width={`${calcVideoSize().width}px`}
                  height={`${calcVideoSize().height}px`}
                />
              )}
            </div>
            {matches.length === 1 ? (
              <div className="flex justify-between">
                <div className="flex gap-2">
                  <div
                    id="video-controls"
                    className="flex gap-1 h-10 w-full px-2 bg-base-100"
                  >
                    <a
                      data-tooltip-id="tt-back60"
                      data-tooltip-content={t("Rewind 60 seconds")}
                    >
                      <BackwardIcon
                        className="btn-toolbar"
                        onClick={() => seekRelative(-60)}
                      />
                    </a>
                    <Tooltip
                      id="tt-back60"
                      place={"bottom-start"}
                      style={{
                        backgroundColor: "hsl(var(--b3))",
                        color: "hsl(var(--bc))",
                      }}
                    />
                    <a
                      data-tooltip-id="tt-back10"
                      data-tooltip-content={t("Rewind 10 seconds")}
                    >
                      <ChevronDoubleLeftIcon
                        className="btn-toolbar"
                        onClick={() => seekRelative(-10)}
                      />
                    </a>
                    <Tooltip
                      id="tt-back10"
                      place={"bottom-start"}
                      style={{
                        backgroundColor: "hsl(var(--b3))",
                        color: "hsl(var(--bc))",
                      }}
                    />
                    <a
                      data-tooltip-id="tt-back1"
                      data-tooltip-content={t("Rewind 1 second")}
                    >
                      <ChevronLeftIcon
                        className="btn-toolbar"
                        onClick={() => seekRelative(-1)}
                      />
                    </a>
                    <Tooltip
                      id="tt-back1"
                      place={"bottom-start"}
                      style={{
                        backgroundColor: "hsl(var(--b3))",
                        color: "hsl(var(--bc))",
                      }}
                    />
                    <a
                      data-tooltip-id="tt-backframe"
                      data-tooltip-content={t("Rewind 1 frame")}
                    >
                      <ChevronLeftMiniIcon
                        className="btn-toolbar"
                        onClick={() => seekRelative(-FRAME_SECONDS)}
                      />
                    </a>
                    <Tooltip
                      id="tt-backframe"
                      place={"bottom-start"}
                      style={{
                        backgroundColor: "hsl(var(--b3))",
                        color: "hsl(var(--bc))",
                      }}
                    />
                    <a
                      data-tooltip-id="tt-forwardframe"
                      data-tooltip-content={t("Forward 1 frame")}
                    >
                      <ChevronRightMiniIcon
                        className="btn-toolbar"
                        onClick={() => seekRelative(FRAME_SECONDS)}
                      />
                    </a>
                    <Tooltip
                      id="tt-forwardframe"
                      place={"bottom-start"}
                      style={{
                        backgroundColor: "hsl(var(--b3))",
                        color: "hsl(var(--bc))",
                      }}
                    />
                    <a
                      data-tooltip-id="tt-forward1"
                      data-tooltip-content={t("Forward 1 second")}
                    >
                      <ChevronRightIcon
                        className="btn-toolbar"
                        onClick={() => seekRelative(1)}
                      />
                    </a>
                    <Tooltip
                      id="tt-forward1"
                      place={"bottom-start"}
                      style={{
                        backgroundColor: "hsl(var(--b3))",
                        color: "hsl(var(--bc))",
                      }}
                    />
                    <a
                      data-tooltip-id="tt-forward10"
                      data-tooltip-content={t("Forward 10 seconds")}
                    >
                      <ChevronDoubleRightIcon
                        className="btn-toolbar"
                        onClick={() => seekRelative(10)}
                      />
                    </a>
                    <Tooltip
                      id="tt-forward10"
                      place={"bottom-start"}
                      style={{
                        backgroundColor: "hsl(var(--b3))",
                        color: "hsl(var(--bc))",
                      }}
                    />
                    <a
                      data-tooltip-id="tt-forward60"
                      data-tooltip-content={t("Forward 60 seconds")}
                    >
                      <ForwardIcon
                        className="btn-toolbar"
                        onClick={() => seekRelative(60)}
                      />
                    </a>
                    <Tooltip
                      id="tt-forward60"
                      place={"bottom-start"}
                      style={{
                        backgroundColor: "hsl(var(--b3))",
                        color: "hsl(var(--bc))",
                      }}
                    />
                  </div>

                  {videoFilePath?.endsWith(".m3u8") && isBehindLive && (
                    <a
                      data-tooltip-id="tt-golive"
                      data-tooltip-content={t("Playback has fallen behind the live broadcast — click to jump to now.")}
                    >
                      <button
                        type="button"
                        className="btn btn-error btn-sm w-28 gap-1 whitespace-nowrap flex-shrink-0 px-3 animate-pulse"
                        onClick={goLive}
                      >
                        <SignalIcon className="h-4 w-4" />
                        {t("Go Live")}
                      </button>
                    </a>
                  )}
                  <Tooltip
                    id="tt-golive"
                    place={"bottom-start"}
                    style={{
                      backgroundColor: "hsl(var(--b3))",
                      color: "hsl(var(--bc))",
                    }}
                  />

                  <a
                    data-tooltip-id="tt-timing"
                    data-tooltip-content={t("Set timing for events")}
                  >
                    <ClockIcon
                      className="btn-toolbar"
                      onClick={() => doTiming()}
                    />
                  </a>
                  <Tooltip
                    id="tt-timing"
                    place={"bottom-start"}
                    style={{
                      backgroundColor: "hsl(var(--b3))",
                      color: "hsl(var(--bc))",
                    }}
                  />
                </div>

                {videoOffset !== 0 ? (
                  <></>
                ) : (
                  // <p>Offset = {videoOffset} secs</p>
                  <></>
                )}
              </div>
            ) : (
              <></>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (!matches || matches.length === 0) {
    return (
      <div className="alert alert-error m-4">
        <span>{t("This report isn't supported on this browser yet — please use Safari.")}</span>
      </div>
    );
  }

  return (
    <>
      <div>
        <Dialog
          open={sidebarOpen}
          onClose={setSidebarOpen}
          className="relative z-50 lg:hidden"
        >
          <DialogBackdrop
            transition
            className="fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0"
          />

          <div className="fixed inset-0 flex">
            <DialogPanel
              transition
              className="relative mr-16 flex w-full max-w-[320px] flex-1 transform transition duration-300 ease-in-out data-[closed]:-translate-x-full"
            >
              <TransitionChild>
                <div className="absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0">
                  <button
                    type="button"
                    onClick={() => setSidebarOpen(false)}
                    className="-m-2.5 p-2.5"
                  >
                    <span className="sr-only">Close sidebar</span>
                    <XMarkIcon
                      aria-hidden="true"
                      className="size-6 text-white"
                    />
                  </button>
                </div>
              </TransitionChild>
              {/* Sidebar component, swap this element with another sidebar if you like */}
              <div className="flex grow flex-col bg-base-100">
                {doLeftSide()}
              </div>
            </DialogPanel>
          </div>
        </Dialog>

        {/* Static sidebar for desktop */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-[320px] lg:flex-col">
          <div className="flex grow flex-col bg-base-100">{doLeftSide()}</div>
        </div>

        <div className="lg:px-4 lg:ml-72 w-fit">
          <div className="sticky top-0 z-40 lg:mx-auto lg:max-w-7xl lg:px-0">
            <div className="flex items-center gap-x-4 border-b border-gray-200 bg-base-100 px-2 shadow-sm sm:gap-x-6 sm:px-0 lg:px-2 lg:shadow-none">
              <button
                type="button"
                onClick={() => setSidebarOpen(true)}
                className="text-base-content/50 lg:hidden"
              >
                <span className="sr-only">Open sidebar</span>
                <Bars3Icon aria-hidden="true" className="size-6" />
              </button>

              <div className="flex flex-1 gap-x-4 self-stretch lg:gap-x-6">
                <form
                  action="#"
                  method="GET"
                  className="grid flex-1 grid-cols-1"
                >
                  <h1 className="text-sm text-center py-2 font-semibold text-base-content/80">
                    {videoTitle()}
                  </h1>
                </form>
                {isLive && (
                  <div className="flex items-center gap-1 py-1">
                    <span className="text-xs font-bold text-white bg-red-700 px-1.5 py-0.5 leading-none">
                      LIVE
                    </span>
                    {liveAutoRefresh ? (
                      <>
                        <span className="text-xs text-base-content/60">
                          {liveRefreshInterval - liveCounter}s
                        </span>
                        <div className="tooltip" data-tip="Turn Auto-refresh Off">
                          <XCircleIcon
                            className="size-5 text-base-content/60 cursor-pointer hover:text-base-content"
                            onClick={() => setLiveAutoRefresh(false)}
                          />
                        </div>
                      </>
                    ) : (
                      <div className="tooltip" data-tip="Turn Auto-refresh On">
                        <CheckCircleIcon
                          className="size-5 text-base-content/60 cursor-pointer hover:text-base-content"
                          onClick={() => setLiveAutoRefresh(true)}
                        />
                      </div>
                    )}
                    <div className="tooltip" data-tip="Refresh Now">
                      <ArrowPathIcon
                        className="size-5 text-base-content/60 cursor-pointer hover:text-base-content"
                        onClick={() => refreshLiveData()}
                      />
                    </div>
                  </div>
                )}
                <div className="flex items-center gap-x-4 lg:gap-x-6">
                  <button
                    type="button"
                    className="btn btn-sm btn-ghost"
                    onClick={() => onBackClick()}
                  >
                    {t("Close")}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <main className="py-2">
            <div className="mx-auto max-w-7xl px-1 sm:pl-0 sm:pr-2 lg:pr-2 lg:pl-8">
              {doRightSide()}
            </div>
          </main>
        </div>
      </div>

      {showFiltersModal && (
        <dialog className="modal z-[9999]" open onKeyDown={(e) => { if (e.key === "Escape") setShowFiltersModal(false); }}>
          <div className="modal-box w-7/12 max-w-5xl h-full">
            <VideoFilters
              allFilters={allFilters}
              matches={matches}
              team={team}
              teamAPlayers={teamAPlayers}
              teamBPlayers={teamBPlayers}
              games={games}
              rotations={rotations}
              eventTypes={eventTypes}
              eventResults={eventResults}
              attackTypes={attackTypes}
              hitTypes={hitTypes}
              blockTypes={blockTypes}
              attackCombos={attackCombos}
              setterCalls={setterCalls}
              powerPlayOptions={powerPlayOptions}
              onDoFilters={(fes) => onFilters(fes)}
              onCancel={() => setShowFiltersModal(false)}
            />
          </div>
          <form method="dialog" className="modal-backdrop" onClick={() => setShowFiltersModal(false)} />
        </dialog>
      )}

      {showTimingModal && (
        <dialog className="modal z-[9999]" open onKeyDown={(e) => { if (e.key === "Escape") setShowTimingModal(false); }}>
          <div className="modal-box sm:w-4/12 w-full max-w-5xl h-[70vh]">
            <h3 className="mb-4 font-bold text-2xl">{t("Timing")}</h3>
            <div className="flex flex-col">
              <div>
                <Timing onClose={() => doCloseTiming()} />
              </div>
            </div>
          </div>
          <form method="dialog" className="modal-backdrop" onClick={() => setShowTimingModal(false)} />
        </dialog>
      )}
    </>
  );
}

export default VideoAnalysis;
